import React from 'react';
import { Loader2 } from 'lucide-react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  message?: string;
  className?: string;
  variant?: 'spinner' | 'dots' | 'pulse';
}

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'title' | 'avatar' | 'image' | 'button' | 'card';
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ 
  size = 'md', 
  message,
  className = '',
  variant = 'spinner'
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12'
  };

  const renderSpinner = () => {
    switch (variant) {
      case 'dots':
        return (
          <div className="loading-dots">
            <div></div>
            <div></div>
            <div></div>
          </div>
        );
      case 'pulse':
        return (
          <div className={`${sizeClasses[size]} bg-navy-400 rounded-full animate-pulse-subtle`} />
        );
      default:
        return (
          <Loader2 className={`${sizeClasses[size]} text-navy-600 animate-spin`} />
        );
    }
  };
  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      {renderSpinner()}
      {message && (
        <p className="mt-3 text-sm text-navy-600 animate-pulse-subtle">
          {message}
        </p>
      )}
    </div>
  );
};

export const Skeleton: React.FC<SkeletonProps> = ({ 
  className = '', 
  variant = 'text' 
}) => {
  const variantClasses = {
    text: 'skeleton-text',
    title: 'skeleton-title',
    avatar: 'skeleton-avatar',
    image: 'skeleton-image',
    button: 'skeleton-button',
    card: 'skeleton h-32 w-full'
  };

  return <div className={`${variantClasses[variant]} ${className}`} />;
};

export const CardSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`card-premium p-6 ${className}`}>
      <div className="flex items-center space-x-4 mb-4">
        <Skeleton variant="avatar" />
        <div className="flex-1">
          <Skeleton variant="title" />
          <Skeleton variant="text" className="w-2/3" />
        </div>
      </div>
      <Skeleton variant="image" className="mb-4" />
      <Skeleton variant="text" />
      <Skeleton variant="text" className="w-3/4" />
      <div className="flex justify-between items-center mt-4">
        <Skeleton variant="text" className="w-1/4" />
        <Skeleton variant="button" />
      </div>
    </div>
  );
};

export const ListSkeleton: React.FC<{ 
  items?: number; 
  className?: string 
}> = ({ items = 3, className = '' }) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {Array.from({ length: items }).map((_, index) => (
        <div key={index} className="card-premium p-4">
          <div className="flex items-center space-x-3">
            <Skeleton variant="avatar" className="h-10 w-10" />
            <div className="flex-1">
              <Skeleton variant="title" className="h-4 w-1/2 mb-2" />
              <Skeleton variant="text" className="h-3 w-3/4" />
            </div>
            <Skeleton variant="button" className="h-8 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
};

export const ButtonLoading: React.FC<{ 
  children: React.ReactNode;
  loading?: boolean;
  className?: string;
  onClick?: () => void;
}> = ({ children, loading = false, className = '', onClick }) => {
  return (
    <button
      className={`btn-elegant-primary ${className} ${loading ? 'cursor-not-allowed opacity-75' : ''}`}
      onClick={onClick}
      disabled={loading}
    >
      {loading ? (
        <div className="flex items-center space-x-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading...</span>
        </div>
      ) : (
        children
      )}
    </button>
  );
};
export default LoadingSpinner;